/**
 * CASEWORK STUDIO — stage 2, the luxury catalog (2026-10-09, Matt: "Go with stage 2, include in
 * it also different profiles for the base"). Character names, never brands (the plumbing / light
 * styles precedent); every choice is generated, real-size geometry (bim-geometry
 * categories/casework/kit.ts), so a new option is a catalog entry + a builder branch.
 *
 * Profiles here are (o, z) rings in FEET in the trimProfiles.ts convention: o = projection out
 * from the face the profile is swept along (0 = the back, against the cabinet), z = up from the
 * profile's base. Rings start at the back-bottom and close up the back face.
 */
import type { Pt2 } from './types'
import { trimProfileRing } from './trimProfiles'

// ---------- door + drawer fronts ----------
export const DOOR_PROFILES = [
  { value: 'slab', label: 'Slab' },
  { value: 'shaker', label: 'Shaker' },
  { value: 'slimShaker', label: 'Slim shaker' },
  { value: 'beadedShaker', label: 'Beaded shaker' },
  { value: 'raised', label: 'Raised panel' },
  { value: 'applied', label: 'Applied moulding' },
  { value: 'beadboard', label: 'Beadboard' },
  { value: 'fluted', label: 'Fluted' },
  { value: 'glass', label: 'Glass' },
  { value: 'glassLites', label: 'Divided-lite glass' },
] as const
/** fronts that take glass — never a drawer */
export const GLASS_PROFILES = ['glass', 'glassLites']

// ---------- hardware ----------
export const PULL_STYLES = [
  { value: 'none', label: 'None' },
  { value: 'knob', label: 'Round knob' },
  { value: 'knobMushroom', label: 'Mushroom knob' },
  { value: 'knobSquare', label: 'Square knob' },
  { value: 'bar', label: 'Bar pull' },
  { value: 'barRound', label: 'Round bar' },
  { value: 'cup', label: 'Cup pull' },
  { value: 'edge', label: 'Edge pull' },
  { value: 'groove', label: 'Routed finger groove' },
  { value: 'ring', label: 'Ring pull' },
  { value: 'latch', label: 'Cabinet latch' },
  { value: 'appliance', label: 'Appliance pull' },
] as const
/** pulls that come in lengths (inches, centre to centre-ish overall); 0 = the style's own */
export const PULL_LENGTHS_IN = [3, 4, 5, 6, 8, 10, 12, 18]
export const DEFAULT_PULL_IN: Record<string, number> = { bar: 6, barRound: 6, edge: 3, appliance: 12, cup: 3 }

/** hardware finishes — the bare metals the renderer already knows (colors.ts BARE_METALS) */
export const HARDWARE_FINISHES = [
  { value: 'nickel', label: 'Polished nickel' },
  { value: 'stainless', label: 'Satin stainless' },
  { value: 'brass', label: 'Polished brass' },
  { value: 'agedBrass', label: 'Unlacquered brass' },
  { value: 'bronze', label: 'Oil-rubbed bronze' },
  { value: 'copper', label: 'Copper' },
  { value: 'blackened', label: 'Matte black' },
] as const

// ---------- base ----------
export const BASE_PROFILES = [
  { value: 'square', label: 'Square' },
  { value: 'eased', label: 'Eased' },
  { value: 'bevel', label: 'Bevel' },
  { value: 'cove', label: 'Cove' },
  { value: 'ogee', label: 'Ogee' },
  { value: 'plinth', label: 'Plinth' },
] as const
export const FOOT_STYLES = [
  { value: 'bun', label: 'Bun feet' },
  { value: 'bracket', label: 'Bracket feet' },
  { value: 'tapered', label: 'Tapered legs' },
] as const
export const BASE_HEIGHTS_IN = [3, 4, 5, 6]

// ---------- crown + light rail ----------
export const CROWN_STYLES = [
  { value: 'none', label: 'None' },
  { value: 'flat', label: 'Flat frieze' },
  { value: 'cove', label: 'Cove crown' },
  { value: 'ogee', label: 'Ogee crown' },
  { value: 'stepped', label: 'Stepped crown' },
] as const
export const CROWN_HEIGHTS_IN = [2.5, 3.5, 4.5, 6]
export const LIGHT_RAILS = [
  { value: 'none', label: 'None' },
  { value: 'square', label: 'Square' },
  { value: 'cove', label: 'Cove' },
  { value: 'bead', label: 'Bead' },
] as const

const ARC = 8
function arc(cx: number, cz: number, ro: number, rz: number, a0: number, a1: number): Pt2[] {
  const out: Pt2[] = []
  for (let k = 0; k <= ARC; k++) {
    const t = a0 + ((a1 - a0) * k) / ARC
    out.push([cx + ro * Math.cos(t), cz + rz * Math.sin(t)])
  }
  return out
}
function clean(ring: Pt2[]): Pt2[] {
  const out: Pt2[] = []
  for (const p of ring) {
    const l = out[out.length - 1]
    if (!l || Math.abs(l[0] - p[0]) > 1e-9 || Math.abs(l[1] - p[1]) > 1e-9) out.push(p)
  }
  return out
}

/** a furniture base board's section, H tall × P proud (feet) */
export function baseProfileRing(kind: string, H: number, P: number): Pt2[] {
  switch (kind) {
    case 'eased': return trimProfileRing('eased', H, P)
    case 'cove': return trimProfileRing('cove', H, P)
    case 'bevel': {
      const b = Math.min(P * 0.7, H * 0.35)
      return [[0, 0], [P, 0], [P, H - b], [P - b, H], [0, H]]
    }
    case 'ogee': {
      // a plinth face with an ogee cap: flat lower face, then a convex then concave curve
      // running back to the cabinet — the classic furniture base
      const f = H * 0.45, r = (H - f) / 2
      return clean([
        [0, 0], [P, 0], [P, f],
        ...arc(P - r * 0.6, f, r * 0.6, r, 0, Math.PI / 2),
        ...arc(P - r * 0.6, f + 2 * r, r * 0.4, r, -Math.PI / 2, -Math.PI),
        [0, H],
      ])
    }
    case 'plinth': {
      // a square plinth with a small stepped cap
      const c = Math.min(H * 0.22, 0.75 / 12)
      return [[0, 0], [P, 0], [P, H - c], [P + c * 0.6, H - c], [P + c * 0.6, H], [0, H]]
    }
    default: return trimProfileRing('board', H, P)
  }
}

/** a crown's section, H tall; it projects at its top */
export function crownProfileRing(kind: string, H: number): Pt2[] {
  const P = H * 0.8
  switch (kind) {
    case 'flat': return trimProfileRing('board', H, 0.75 / 12)
    case 'cove': return trimProfileRing('crown', H, P)
    case 'ogee': {
      const f = H * 0.12, r = (H - 2 * f) / 2
      return clean([
        [0, 0], [f, 0], [f, f],
        ...arc(f, f + r, r * 0.9, r, -Math.PI / 2, 0),
        ...arc(P, f + r, P - f - r * 0.9, r, Math.PI, Math.PI / 2),
        [P, H], [0, H],
      ])
    }
    case 'stepped': {
      // a frieze board with a coved crown stacked on it
      const fb = H * 0.4, s = 0.75 / 12
      const top = trimProfileRing('crown', H - fb, P - s).map(([o, z]) => [o + s, z + fb] as Pt2)
      return clean([[0, 0], [s, 0], [s, fb], ...top.slice(1, -1), [0, H]])
    }
    default: return []
  }
}

/** a light rail's section, H tall, hanging DOWN from the cabinet bottom (z ≤ 0) */
export function lightRailRing(kind: string): Pt2[] {
  const H = 1.5 / 12, P = 0.75 / 12
  const ring = kind === 'cove' ? trimProfileRing('cove', H, P)
    : kind === 'bead' ? trimProfileRing('bullnose', H, P)
      : trimProfileRing('board', H, P)
  return ring.map(([o, z]) => [o, -z] as Pt2)
}

// ---------- STAGE 3: interiors, power, lighting (2026-10-09) ----------
/** what fills a box's DOOR section inside */
export const INTERIOR_FITS = [
  { value: 'shelves', label: 'Shelves' },
  { value: 'rollouts', label: 'Roll-out trays' },
  { value: 'trash', label: 'Trash pull-out' },
  { value: 'spice', label: 'Spice pull-out' },
  { value: 'trays', label: 'Tray dividers' },
  { value: 'plateRack', label: 'Plate rack' },
  { value: 'pantry', label: 'Pantry roll-outs' },
  { value: 'garage', label: 'Appliance garage' },
] as const
/** what organizes a box's DRAWERS (the top drawer takes a cutlery / knife / spice insert) */
export const DRAWER_INSERTS = [
  { value: 'none', label: 'None' },
  { value: 'cutlery', label: 'Cutlery' },
  { value: 'knife', label: 'Knife block' },
  { value: 'utensil', label: 'Utensils' },
  { value: 'spice', label: 'Spice tiers' },
  { value: 'pegs', label: 'Peg organizer' },
] as const
export const LIGHT_TEMPS_K = [2700, 3000, 3500]

// ---------- STAGE 4: countertops + backsplash (2026-10-09) ----------
/** slab thicknesses (inches): a porcelain slab, 3 cm stone, the firm's 1-1/2", a built-up 2", a 3" block */
export const COUNTER_THICKNESSES_IN = [0.75, 1.25, 1.5, 2, 3]
export const COUNTER_EDGES = [
  { value: 'eased', label: 'Eased' },
  { value: 'bevel', label: 'Beveled' },
  { value: 'halfBullnose', label: 'Half bullnose' },
  { value: 'fullBullnose', label: 'Full bullnose' },
  { value: 'ogee', label: 'Ogee' },
  { value: 'mitered', label: 'Mitered apron' },
] as const
/** waterfall ends, named as seen standing in the room facing the run */
export const COUNTER_WATERFALLS = [
  { value: 'none', label: 'None' },
  { value: 'left', label: 'Left end' },
  { value: 'right', label: 'Right end' },
  { value: 'both', label: 'Both ends' },
] as const
export const BACKSPLASHES = [
  { value: 'none', label: 'None' },
  { value: '4', label: '4"' },
  { value: '6', label: '6"' },
  { value: 'full', label: 'Full height' },
] as const
/** a mitered apron's whole visible edge (inches) */
export const MITERED_APRON_IN = 2.5
/** a backsplash slab's thickness (feet) */
export const SPLASH_T_FT = 0.75 / 12
/** full-height backsplash with nothing above it: this far over the counter (feet) */
export const SPLASH_FULL_DEFAULT_FT = 18 / 12

/**
 * The edge profile as stacked TIERS (feet, measured from the slab's underside): each tier is
 * a band of the slab whose exposed edges sit `inset` back from the slab's outer edge. The
 * counter join welds each tier on its own, so the profile reads in 3D, sections and
 * elevations while every slab stays a plain extruded footprint. The mitered apron is a full
 * slab here (its drop below the slab is built separately).
 */
/**
 * COUNTER EDGES, TRUE TO THEIR NAME (2026-10-09): the edge PROFILE as a continuous curve — how far
 * (feet) the edge sits back from the slab's outer edge at height `z` above its underside. The 3D
 * counter sweeps exactly this (bim-geometry counterLoft.ts); the drawings' tiers sample it.
 *   eased         a 1/8" round on the top and bottom arrises (the stone shop's standard)
 *   bevel         a 3/8" 45° chamfer off the top
 *   halfBullnose  the top rounded over on a radius of 4/5 the thickness, the bottom square
 *   fullBullnose  a full half-round, top to bottom
 *   ogee          an S — a cove rising into a round-over across the top two thirds
 */
export function counterEdgeInsetAt(edge: string, T: number, z: number): number {
  const zz = Math.max(0, Math.min(T, z))
  const round = (r: number, d: number) => r - Math.sqrt(Math.max(0, r * r - d * d))   // a quarter round, d into it
  if (edge === 'eased') {
    const r = Math.min(0.125 / 12, T / 3)
    if (zz > T - r) return round(r, zz - (T - r))
    if (zz < r) return round(r, r - zz)
    return 0
  }
  if (edge === 'bevel') { const b = Math.min(0.375 / 12, T * 0.45); return zz > T - b ? zz - (T - b) : 0 }
  if (edge === 'halfBullnose') { const r = T * 0.8; return zz > T - r ? round(r, zz - (T - r)) : 0 }
  if (edge === 'fullBullnose') { const c = T / 2; return round(c, zz - c) }
  if (edge === 'ogee') {
    const z1 = T * 0.33, A = T * 0.42
    if (zz <= z1) return 0
    const s = (zz - z1) / (T - z1)
    return A * (1 - Math.cos(Math.PI * s)) / 2
  }
  return 0
}

export function counterEdgeTiers(edge: string, T: number): { z0: number; z1: number; inset: number }[] {
  const band = (n: number, f: (z: number) => number) => {
    const out: { z0: number; z1: number; inset: number }[] = []
    for (let i = 0; i < n; i++) {
      const z0 = (T * i) / n, z1 = (T * (i + 1)) / n
      out.push({ z0, z1, inset: Math.max(0, f((z0 + z1) / 2)) })
    }
    return out
  }
  // the drawings' bands sample the one profile curve (the 3D counter sweeps it smooth)
  if (edge === 'bevel' || edge === 'halfBullnose' || edge === 'fullBullnose' || edge === 'ogee') {
    return band(6, (z) => counterEdgeInsetAt(edge, T, z))
  }
  return [{ z0: 0, z1: T, inset: 0 }]
}
/** the color an LED of this temperature reads as (the Studio's glow; approximate) */
export function ledHex(k: number): string {
  return k <= 2700 ? '#ffc98a' : k <= 3000 ? '#ffd9a8' : '#fff0d6'
}
/** shelves a box gets when its count is left on auto */
export function autoShelfCount(style: string, innerH: number): number {
  if (style === 'upper') return Math.max(1, Math.round(innerH / 1.1) - 1)
  if (style === 'tall') return Math.max(3, Math.round(innerH / 1.25) - 1)
  return 1
}
